import { useEffect, useState } from 'react'
import { adminService } from 'core/services/adminService'
import { formatNumbers } from 'core/helpers/format-numbers'
import {
    BarChart, Bar, LineChart, Line,
    XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid
} from 'recharts'

import c from './assets/sass/stats.module.scss'

const formatDay = (dateStr) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`
}

const ChartTooltipContent = ({ active, payload, label, suffix = '' }) => {
    if (!active || !payload?.length) return null
    return (
        <div className={c.chartTooltip}>
            <span className={c.chartTooltipLabel}>{label}</span>
            <span className={c.chartTooltipValue}>{formatNumbers(payload[0].value)}{suffix}</span>
        </div>
    )
}

export const Stats = () => {
    const [stats, setStats] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        loadStats()
    }, [])

    const loadStats = async () => {
        try {
            const data = await adminService.getStats()
            setStats(data.stats)
        } catch (error) {
            console.error('Failed to load stats:', error)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return <div className={c.loading}>Загрузка...</div>
    }

    if (!stats) {
        return <div className={c.error}>Ошибка загрузки статистики</div>
    }

    const items = [
        {
            label: 'Онлайн сейчас',
            value: formatNumbers(stats.onlineNow),
            icon: '🔴',
        },
        {
            label: 'Визиты (24ч)',
            value: formatNumbers(stats.visits24h),
            icon: '👁',
        },
        {
            label: 'Всего пользователей',
            value: formatNumbers(stats.totalUsers),
            icon: '👥',
        },
        {
            label: 'Активных (24ч)',
            value: formatNumbers(stats.activeUsers),
            icon: '🟢',
        },
        {
            label: 'Новых сегодня',
            value: formatNumbers(stats.newToday),
            icon: '✨',
        },
        {
            label: 'Рефералы (24ч)',
            value: formatNumbers(stats.referrals24h),
            icon: '🤝',
        },
        {
            label: 'Куплено кейсов',
            value: formatNumbers(stats.casesOpened),
            icon: '🎁',
        },
        {
            label: 'Бесплатные кейсы',
            value: formatNumbers(stats.freeCasesOpened),
            icon: '🆓',
        },
        {
            label: 'Активных заданий',
            value: formatNumbers(stats.activeTasks),
            icon: '✅',
        },
        {
            label: 'Выполнено заданий',
            value: formatNumbers(stats.tasksCompleted),
            icon: '📋',
        },
        {
            label: 'Забрано подарков',
            value: formatNumbers(stats.adventClaimed),
            icon: '📅',
        },
        {
            label: 'Оборот монет',
            value: formatNumbers(parseInt(stats.coinsCirculation)),
            icon: '💰',
        },
    ]

    const reg7d = (stats.registrations7d || []).map(r => ({
        day: formatDay(r.day),
        count: r.count
    }))

    const coins7d = (stats.coins7d || []).map(r => ({
        day: formatDay(r.day),
        total: r.total
    }))

    return (
        <div className={c.component}>
            <div className={c.grid}>
                {items.map((item, index) => (
                    <div key={index} className={c.card}>
                        <span className={c.icon}>{item.icon}</span>
                        <div className={c.info}>
                            <span className={c.value}>{item.value}</span>
                            <span className={c.label}>{item.label}</span>
                        </div>
                    </div>
                ))}
            </div>

            {reg7d.length > 0 && (
                <div className={c.chartSection}>
                    <h3 className={c.chartTitle}>Регистрации за 7 дней</h3>
                    <div className={c.chartWrapper}>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={reg7d}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="day" stroke="rgba(255,255,255,0.5)" fontSize={11} />
                                <YAxis stroke="rgba(255,255,255,0.5)" fontSize={11} allowDecimals={false} />
                                <Tooltip content={<ChartTooltipContent />} />
                                <Bar dataKey="count" fill="#FFD159" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {coins7d.length > 0 && (
                <div className={c.chartSection}>
                    <h3 className={c.chartTitle}>Оборот монет за 7 дней</h3>
                    <div className={c.chartWrapper}>
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={coins7d}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                <XAxis dataKey="day" stroke="rgba(255,255,255,0.5)" fontSize={11} />
                                <YAxis stroke="rgba(255,255,255,0.5)" fontSize={11} />
                                <Tooltip content={<ChartTooltipContent />} />
                                <Line type="monotone" dataKey="total" stroke="#FE4A32" strokeWidth={2} dot={{ fill: '#FE4A32', r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}
        </div>
    )
}
